import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApplications } from '../../context/ApplicationContext';
import { useWorkflow } from '../../context/WorkflowContext';
import AdminLayout from '../../components/admin/AdminLayout';
import Card, { CardHeader, CardBody } from '../../components/Card';
import Button from '../../components/Button';
import WorkflowApprovalModal from '../../components/admin/WorkflowApprovalModal';
import { FileTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, SearchIcon, FilterIcon, CalendarIcon, BuildingIcon, CreditCardIcon, UserIcon, ChevronRightIcon, TagIcon, BarChart4Icon, UserCheckIcon, ArrowRightIcon, MessageSquareIcon, PlusIcon, UploadIcon, DownloadIcon, MailIcon, PhoneIcon } from 'lucide-react';
const ApplicationManagement: React.FC = () => {
  const {
    applications,
    getApplicationStats,
    updateApplicationStatus,
    addCommentToApplication
  } = useApplications();
  const {
    getWorkflowInstanceByApplicationId,
    getAssignedWorkflowSteps
  } = useWorkflow();
  const navigate = useNavigate();
  const [filteredApplications, setFilteredApplications] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [mdaFilter, setMdaFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('date-desc');
  const [applicationWorkflows, setApplicationWorkflows] = useState<Record<string, any>>({});
  const [assignedWorkflows, setAssignedWorkflows] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState<'all' | 'assigned'>('all');
  const [comment, setComment] = useState('');
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  // Modal state
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(null);
  useEffect(() => {
    // Get workflow instances for each application
    const workflows: Record<string, any> = {};
    applications.forEach(app => {
      const workflow = getWorkflowInstanceByApplicationId(app.id);
      if (workflow) {
        workflows[app.id] = workflow;
      }
    });
    setApplicationWorkflows(workflows);
    // Get assigned workflows
    setAssignedWorkflows(getAssignedWorkflowSteps());
    // Apply initial filters
    applyFilters(applications, searchQuery, statusFilter, mdaFilter, sortBy);
  }, [applications, getWorkflowInstanceByApplicationId, getAssignedWorkflowSteps]);
  const applyFilters = (apps: any[], query: string, status: string, mda: string, sort: string) => {
    // Filter by search query
    let filtered = [...apps];
    if (query) {
      const lowercaseQuery = query.toLowerCase();
      filtered = filtered.filter(app => app.serviceName.toLowerCase().includes(lowercaseQuery) || app.referenceNumber.toLowerCase().includes(lowercaseQuery) || app.userName.toLowerCase().includes(lowercaseQuery) || app.mdaName.toLowerCase().includes(lowercaseQuery));
    }
    // Filter by status
    if (status !== 'all') {
      filtered = filtered.filter(app => app.status === status);
    }
    // Filter by MDA
    if (mda !== 'all') {
      filtered = filtered.filter(app => app.mdaId === mda);
    }
    // Sort applications
    switch (sort) {
      case 'date-desc':
        filtered.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
        break;
      case 'date-asc':
        filtered.sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.serviceName.localeCompare(b.serviceName));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.serviceName.localeCompare(a.serviceName));
        break;
      default:
        break;
    }
    setFilteredApplications(filtered);
  };
  useEffect(() => {
    applyFilters(applications, searchQuery, statusFilter, mdaFilter, sortBy);
  }, [searchQuery, statusFilter, mdaFilter, sortBy, applications]);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    applyFilters(applications, searchQuery, statusFilter, mdaFilter, sortBy);
  };
  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim() || !selectedApplicationId) return;
    setIsSubmittingComment(true);
    // Find the application to get user info
    const application = applications.find(app => app.id === selectedApplicationId);
    if (!application) {
      setIsSubmittingComment(false);
      return;
    }
    // Add the comment
    addCommentToApplication(selectedApplicationId, comment, 'admin-user-id',
    // In a real app, this would be the admin's ID
    'Administrator' // In a real app, this would be the admin's name
    );
    // Reset form
    setComment('');
    setSelectedApplicationId(null);
    setIsSubmittingComment(false);
  };
  const openApprovalModal = (workflow: any) => {
    setSelectedWorkflow(workflow);
    setIsApprovalModalOpen(true);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1247">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-1248" />
            Approved
          </span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1249">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-1250" />
            Rejected
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1251">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-1252" />
            Pending
          </span>;
      case 'processing':
      case 'under_review':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1253">
            <BarChart4Icon className="w-3 h-3 mr-1" data-id="element-1254" />
            Processing
          </span>;
      case 'awaiting_payment':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-1255">
            <CreditCardIcon className="w-3 h-3 mr-1" data-id="element-1256" />
            Awaiting Payment
          </span>;
      case 'awaiting_documents':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-1257">
            <FileTextIcon className="w-3 h-3 mr-1" data-id="element-1258" />
            Awaiting Documents
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1259">
            <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-1260" />
            {status.replace('_', ' ').toUpperCase()}
          </span>;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Get unique MDAs for the filter dropdown
  const uniqueMdas = Array.from(new Set(applications.map(app => app.mdaId))).map(mdaId => {
    const app = applications.find(a => a.mdaId === mdaId);
    return {
      id: mdaId,
      name: app ? app.mdaName : 'Unknown MDA'
    };
  });
  const stats = getApplicationStats();
  return <AdminLayout data-id="element-1261">
      <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen" data-id="element-1262">
        <div className="max-w-7xl mx-auto" data-id="element-1263">
          <div className="mb-6" data-id="element-1264">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1265">
              Application Management
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-1266">
              View, track, and manage all service applications across MDAs.
            </p>
          </div>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-id="element-1267">
            <Card data-id="element-1268">
              <CardBody className="p-4" data-id="element-1269">
                <div className="flex flex-col items-center" data-id="element-1270">
                  <div className="text-gray-500 text-sm mb-1" data-id="element-1271">Total</div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-1272">
                    {stats.total}
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card data-id="element-1273">
              <CardBody className="p-4 bg-yellow-50" data-id="element-1274">
                <div className="flex flex-col items-center" data-id="element-1275">
                  <div className="text-yellow-700 text-sm mb-1" data-id="element-1276">Pending</div>
                  <div className="text-2xl font-bold text-yellow-700" data-id="element-1277">
                    {stats.pending}
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card data-id="element-1278">
              <CardBody className="p-4 bg-blue-50" data-id="element-1279">
                <div className="flex flex-col items-center" data-id="element-1280">
                  <div className="text-blue-700 text-sm mb-1" data-id="element-1281">Processing</div>
                  <div className="text-2xl font-bold text-blue-700" data-id="element-1282">
                    {stats.processing}
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card data-id="element-1283">
              <CardBody className="p-4 bg-purple-50" data-id="element-1284">
                <div className="flex flex-col items-center" data-id="element-1285">
                  <div className="text-purple-700 text-sm mb-1" data-id="element-1286">
                    Awaiting Payment
                  </div>
                  <div className="text-2xl font-bold text-purple-700" data-id="element-1287">
                    {applications.filter(app => app.status === 'awaiting_payment').length}
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card data-id="element-1288">
              <CardBody className="p-4 bg-green-50" data-id="element-1289">
                <div className="flex flex-col items-center" data-id="element-1290">
                  <div className="text-green-700 text-sm mb-1" data-id="element-1291">Approved</div>
                  <div className="text-2xl font-bold text-green-700" data-id="element-1292">
                    {stats.approved}
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card data-id="element-1293">
              <CardBody className="p-4 bg-red-50" data-id="element-1294">
                <div className="flex flex-col items-center" data-id="element-1295">
                  <div className="text-red-700 text-sm mb-1" data-id="element-1296">Rejected</div>
                  <div className="text-2xl font-bold text-red-700" data-id="element-1297">
                    {stats.rejected}
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
          {/* Tabs */}
          <div className="mb-6" data-id="element-1298">
            <div className="flex space-x-1 border-b border-gray-200" data-id="element-1299">
              <button onClick={() => setSelectedTab('all')} className={`px-4 py-2 text-sm font-medium ${selectedTab === 'all' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1300">
                All Applications
              </button>
              <button onClick={() => setSelectedTab('assigned')} className={`px-4 py-2 text-sm font-medium flex items-center ${selectedTab === 'assigned' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1301">
                Assigned to Me
                {assignedWorkflows.length > 0 && <span className="ml-2 bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full" data-id="element-1302">
                    {assignedWorkflows.length}
                  </span>}
              </button>
            </div>
          </div>
          {/* Filters */}
          <Card className="mb-6" data-id="element-1303">
            <CardBody className="p-4" data-id="element-1304">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0" data-id="element-1305">
                <form onSubmit={handleSearch} className="relative flex-grow max-w-md" data-id="element-1306">
                  <input type="text" placeholder="Search applications..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" data-id="element-1307" />
                  <SearchIcon size={20} className="absolute left-3 top-2.5 text-gray-400" data-id="element-1308" />
                  <button type="submit" className="absolute right-3 top-2.5 text-gray-400 hover:text-green-600" aria-label="Search" data-id="element-1309">
                    <span className="sr-only" data-id="element-1310">Search</span>
                  </button>
                </form>
                <div className="flex flex-wrap items-center space-x-2" data-id="element-1311">
                  <div className="flex items-center" data-id="element-1312">
                    <FilterIcon size={16} className="mr-2 text-gray-500" data-id="element-1313" />
                    <span className="text-sm text-gray-700 mr-2" data-id="element-1314">Status:</span>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" data-id="element-1315">
                      <option value="all" data-id="element-1316">All</option>
                      <option value="awaiting_payment" data-id="element-1317">Awaiting Payment</option>
                      <option value="pending" data-id="element-1318">Pending</option>
                      <option value="processing" data-id="element-1319">Processing</option>
                      <option value="under_review" data-id="element-1320">Under Review</option>
                      <option value="approved" data-id="element-1321">Approved</option>
                      <option value="rejected" data-id="element-1322">Rejected</option>
                    </select>
                  </div>
                  <div className="flex items-center" data-id="element-1323">
                    <BuildingIcon size={16} className="mr-2 text-gray-500" data-id="element-1324" />
                    <span className="text-sm text-gray-700 mr-2" data-id="element-1325">MDA:</span>
                    <select value={mdaFilter} onChange={e => setMdaFilter(e.target.value)} className="border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" data-id="element-1326">
                      <option value="all" data-id="element-1327">All MDAs</option>
                      {uniqueMdas.map(mda => <option key={mda.id} value={mda.id} data-id="element-1328">
                          {mda.name}
                        </option>)}
                    </select>
                  </div>
                  <div className="flex items-center" data-id="element-1329">
                    <span className="text-sm text-gray-700 mr-2" data-id="element-1330">Sort:</span>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" data-id="element-1331">
                      <option value="date-desc" data-id="element-1332">Newest First</option>
                      <option value="date-asc" data-id="element-1333">Oldest First</option>
                      <option value="name-asc" data-id="element-1334">Name (A-Z)</option>
                      <option value="name-desc" data-id="element-1335">Name (Z-A)</option>
                    </select>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
          {/* Applications List */}
          {selectedTab === 'assigned' ? assignedWorkflows.length === 0 ? <Card data-id="element-1336">
                <CardBody className="p-8 text-center" data-id="element-1337">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100" data-id="element-1338">
                    <UserCheckIcon className="h-6 w-6 text-green-600" data-id="element-1339" />
                  </div>
                  <h3 className="mt-2 text-lg font-medium text-gray-900" data-id="element-1340">
                    No applications assigned to you
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-1341">
                    You don't have any applications assigned for approval at
                    this time.
                  </p>
                </CardBody>
              </Card> : <div className="space-y-4" data-id="element-1342">
                {assignedWorkflows.map(item => <Card key={item.instance.id} className="hover:shadow-md transition-shadow border-l-4 border-blue-500" data-id="element-1343">
                    <CardBody className="p-4" data-id="element-1344">
                      <div className="flex flex-col md:flex-row md:items-center justify-between" data-id="element-1345">
                        <div data-id="element-1346">
                          <h3 className="text-lg font-medium text-gray-900" data-id="element-1347">
                            {item.application.serviceName}
                          </h3>
                          <div className="flex flex-wrap items-center mt-1 space-x-2" data-id="element-1348">
                            <span className="text-xs text-gray-500 flex items-center" data-id="element-1349">
                              <TagIcon className="h-3 w-3 mr-1" data-id="element-1350" />
                              {item.application.referenceNumber}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center" data-id="element-1351">
                              <UserCheckIcon className="h-3 w-3 mr-1" data-id="element-1352" />
                              {item.application.userName}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center" data-id="element-1353">
                              <CalendarIcon className="h-3 w-3 mr-1" data-id="element-1354" />
                              {formatDate(item.application.submittedAt)}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 md:mt-0 flex flex-col items-start md:items-end" data-id="element-1355">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1356">
                            <ClockIcon className="w-3 h-3 mr-1" data-id="element-1357" />
                            Awaiting Your Approval
                          </span>
                          <div className="mt-1 text-xs text-gray-500" data-id="element-1358">
                            Current step: {item.step.name}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-1359">
                        <div className="flex items-center justify-between mb-2" data-id="element-1360">
                          <span className="text-xs font-medium text-gray-700" data-id="element-1361">
                            Approval Progress
                          </span>
                          <span className="text-xs text-gray-500" data-id="element-1362">
                            Step {item.instance.currentStepIndex + 1} of{' '}
                            {item.instance.steps.length}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1363">
                          <div className="bg-green-600 h-2.5 rounded-full" style={{
                    width: `${(item.instance.currentStepIndex + 1) / item.instance.steps.length * 100}%`
                  }} data-id="element-1364"></div>
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap items-center justify-end space-x-2" data-id="element-1365">
                        <Button variant="outline" size="sm" onClick={() => navigate(`/admin/applications/${item.application.id}`)} className="flex items-center" data-id="element-1366">
                          <ChevronRightIcon className="h-4 w-4 mr-1" data-id="element-1367" />
                          View Details
                        </Button>
                        <Button variant="primary" size="sm" onClick={() => openApprovalModal({
                  instanceId: item.instance.id,
                  stepId: item.step.id,
                  applicationId: item.application.id,
                  referenceNumber: item.application.referenceNumber,
                  serviceName: item.application.serviceName,
                  currentStepName: item.step.name
                })} className="flex items-center" data-id="element-1368">
                          <CheckIcon className="h-4 w-4 mr-1" data-id="element-1369" />
                          Process Approval
                        </Button>
                      </div>
                    </CardBody>
                  </Card>)}
              </div> : filteredApplications.length === 0 ? <Card data-id="element-1370">
              <CardBody className="p-8 text-center" data-id="element-1371">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100" data-id="element-1372">
                  <FileTextIcon className="h-6 w-6 text-green-600" data-id="element-1373" />
                </div>
                <h3 className="mt-2 text-lg font-medium text-gray-900" data-id="element-1374">
                  No applications found
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-1375">
                  No applications match your current filters.
                </p>
              </CardBody>
            </Card> : <div className="space-y-4" data-id="element-1376">
              {filteredApplications.map(application => <Card key={application.id} className="hover:shadow-md transition-shadow" data-id="element-1377">
                  <CardBody className="p-4" data-id="element-1378">
                    <div className="flex flex-col md:flex-row md:items-center justify-between" data-id="element-1379">
                      <div data-id="element-1380">
                        <h3 className="text-lg font-medium text-gray-900" data-id="element-1381">
                          {application.serviceName}
                        </h3>
                        <div className="flex flex-wrap items-center mt-1 space-x-2" data-id="element-1382">
                          <span className="text-xs text-gray-500 flex items-center" data-id="element-1383">
                            <TagIcon className="h-3 w-3 mr-1" data-id="element-1384" />
                            {application.referenceNumber}
                          </span>
                          <span className="text-xs text-gray-500 flex items-center" data-id="element-1385">
                            <UserCheckIcon className="h-3 w-3 mr-1" data-id="element-1386" />
                            {application.userName}
                          </span>
                          <span className="text-xs text-gray-500 flex items-center" data-id="element-1387">
                            <CalendarIcon className="h-3 w-3 mr-1" data-id="element-1388" />
                            {formatDate(application.submittedAt)}
                          </span>
                          <span className="text-xs text-gray-500 flex items-center" data-id="element-1389">
                            <BuildingIcon className="h-3 w-3 mr-1" data-id="element-1390" />
                            {application.mdaName}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 md:mt-0 flex flex-col items-start md:items-end" data-id="element-1391">
                        <div className="flex items-center" data-id="element-1392">
                          {getStatusBadge(application.status)}
                          {application.isPaid ? <span className="ml-2 text-xs text-green-600 flex items-center" data-id="element-1393">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-1394" />
                              Paid
                            </span> : <span className="ml-2 text-xs text-red-600 flex items-center" data-id="element-1395">
                              <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-1396" />
                              Unpaid
                            </span>}
                        </div>
                        <div className="mt-1 text-xs text-gray-500" data-id="element-1397">
                          Last updated: {formatDate(application.updatedAt)}
                        </div>
                      </div>
                    </div>
                    {/* Workflow Progress */}
                    {applicationWorkflows[application.id] && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-1398">
                        <div className="flex items-center justify-between mb-2" data-id="element-1399">
                          <span className="text-xs font-medium text-gray-700" data-id="element-1400">
                            Approval Progress
                          </span>
                          <span className="text-xs text-gray-500" data-id="element-1401">
                            Step{' '}
                            {applicationWorkflows[application.id].currentStepIndex + 1}{' '}
                            of{' '}
                            {applicationWorkflows[application.id].steps.length}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1402">
                          <div className="bg-green-600 h-2.5 rounded-full" style={{
                    width: `${(applicationWorkflows[application.id].currentStepIndex + 1) / applicationWorkflows[application.id].steps.length * 100}%`
                  }} data-id="element-1403"></div>
                        </div>
                        <div className="mt-2" data-id="element-1404">
                          <span className="text-xs text-gray-600" data-id="element-1405">
                            Current step:{' '}
                            <span className="font-medium" data-id="element-1406">
                              {applicationWorkflows[application.id].steps[applicationWorkflows[application.id].currentStepIndex].name}
                            </span>
                          </span>
                        </div>
                      </div>}
                    {/* User Contact Info */}
                    <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-1407">
                      <div className="flex flex-wrap gap-3" data-id="element-1408">
                        <span className="text-xs text-gray-600 flex items-center" data-id="element-1409">
                          <MailIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-1410" />
                          {application.userEmail}
                        </span>
                        <span className="text-xs text-gray-600 flex items-center" data-id="element-1411">
                          <PhoneIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-1412" />
                          {application.formData?.phone || 'N/A'}
                        </span>
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="mt-4 flex flex-wrap items-center justify-end space-x-2" data-id="element-1413">
                      <Button variant="outline" size="sm" onClick={() => setSelectedApplicationId(application.id)} className="flex items-center" data-id="element-1414">
                        <MessageSquareIcon className="h-4 w-4 mr-1" data-id="element-1415" />
                        Add Comment
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/admin/applications/${application.id}`)} className="flex items-center" data-id="element-1416">
                        <ChevronRightIcon className="h-4 w-4 mr-1" data-id="element-1417" />
                        View Details
                      </Button>
                      {application.status === 'pending' && applicationWorkflows[application.id] && <Button variant="primary" size="sm" onClick={() => openApprovalModal({
                  instanceId: applicationWorkflows[application.id].id,
                  stepId: applicationWorkflows[application.id].steps[applicationWorkflows[application.id].currentStepIndex].id,
                  applicationId: application.id,
                  referenceNumber: application.referenceNumber,
                  serviceName: application.serviceName,
                  currentStepName: applicationWorkflows[application.id].steps[applicationWorkflows[application.id].currentStepIndex].name
                })} className="flex items-center" data-id="element-1418">
                            <ArrowRightIcon className="h-4 w-4 mr-1" data-id="element-1419" />
                            Process
                          </Button>}
                    </div>
                  </CardBody>
                </Card>)}
            </div>}
          {/* Add Comment Modal */}
          {selectedApplicationId && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1420">
              <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-1421">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-1422">
                  <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-1423"></div>
                </div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-1424">
                  &#8203;
                </span>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-1425">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-1426">
                    <div className="sm:flex sm:items-start" data-id="element-1427">
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-1428">
                        <div className="flex justify-between items-center" data-id="element-1429">
                          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-1430">
                            Add Comment
                          </h3>
                          <button onClick={() => setSelectedApplicationId(null)} className="text-gray-400 hover:text-gray-500" data-id="element-1431">
                            <XIcon className="h-5 w-5" data-id="element-1432" />
                          </button>
                        </div>
                        <form onSubmit={handleCommentSubmit} className="mt-4" data-id="element-1433">
                          <div className="mb-4" data-id="element-1434">
                            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1435">
                              Comment
                            </label>
                            <textarea value={comment} onChange={e => setComment(e.target.value)} className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border border-gray-300 rounded-md p-2" rows={4} placeholder="Add your comment here" required data-id="element-1436" />
                          </div>
                          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse" data-id="element-1437">
                            <Button type="submit" variant="primary" disabled={isSubmittingComment || !comment.trim()} className="w-full sm:w-auto sm:ml-3" data-id="element-1438">
                              {isSubmittingComment ? 'Submitting...' : 'Add Comment'}
                            </Button>
                            <Button type="button" variant="outline" onClick={() => setSelectedApplicationId(null)} className="mt-3 w-full sm:mt-0 sm:w-auto" data-id="element-1439">
                              Cancel
                            </Button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {/* Workflow Approval Modal */}
          {isApprovalModalOpen && selectedWorkflow && <WorkflowApprovalModal isOpen={isApprovalModalOpen} onClose={() => setIsApprovalModalOpen(false)} instanceId={selectedWorkflow.instanceId} stepId={selectedWorkflow.stepId} applicationId={selectedWorkflow.applicationId} referenceNumber={selectedWorkflow.referenceNumber} serviceName={selectedWorkflow.serviceName} currentStepName={selectedWorkflow.currentStepName} data-id="element-1440" />}
        </div>
      </div>
    </AdminLayout>;
};
export default ApplicationManagement;